<script lang="ts">
    import {toast} from "svelte-sonner";
    import {useForm} from "@inertiajs/svelte";
    import Modal from "@/components/Modal.svelte";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import FormBlock from "@/components/Form/FormBlock.svelte";
    import {Button} from "@/components/ui/button";
    import Input from "@/components/Form/Inputs/Input.svelte";
    import type Blocks from '@/schemas/public/Blocks.ts';
    import type {Field} from "@/types/types";
    import RelatedSingle from "@/components/Inputs/RelatedSingle.svelte";
    import CustomerTable from "@/components/customers/CustomerTable.svelte";
    import {SvelteMap} from "svelte/reactivity";
    import MultiRelated from "@/components/Inputs/MultiRelated.svelte";
    import SiteTable from "@/components/sites/SiteTable.svelte";
    import Textarea from "@/components/Inputs/Textarea.svelte";
    import Select from "@/components/Inputs/Select.svelte";
    import JobTable from "@/components/jobs/JobTable.svelte";
    import AddressLookup from "@/components/Inputs/AddressLookup.svelte";
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import DateOnly from '@/components/Inputs/Date.svelte';
    import { parseFieldOptions } from '@/lib/fieldUtils';
    import { createDateDurationSync } from '@/lib/dateDurationCalculator.svelte';
    import { transformToNestedStructure, normalizeCheckboxValues } from '@/lib/formTransformers';
    import { router } from '@inertiajs/svelte';
    import Debug from '@/components/Debug.svelte';
    import { isFieldRequiredForStatus, statusesThatRequireJob } from '@/lib/leadStatusRules';
    import Checkbox from '@/components/Inputs/Checkbox.svelte';
    import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
    import { formatDateLocale } from '@/lib/dateUtils';

    type DuplicateLead = {
        id: string;
        reference: string;
        name: string;
        address: string | null;
        start_date: string | null;
        customers: string[];
    };

    /** Existing leads found at the same postcode (warning only, never blocks save). */
    let duplicateLeads = $state<DuplicateLead[]>([]);
    let duplicateDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * Look up existing leads sharing this postcode so we can warn about a
     * possible duplicate. Normalizes to match the backend (strip spaces,
     * uppercase) and skips anything shorter than a full postcode.
     */
    async function checkPostcodeDuplicates(postcode: string | null | undefined) {
        const normalized = (postcode ?? '').replace(/\s+/g, '').toUpperCase();
        if (normalized.length < 5) {
            duplicateLeads = [];
            return;
        }

        try {
            const response = await fetch(
                `/web-api/leads/check-duplicates?postcode=${encodeURIComponent(postcode ?? '')}`,
                { headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } },
            );
            if (!response.ok) {
                duplicateLeads = [];
                return;
            }
            const json = await response.json();
            duplicateLeads = json.leads ?? [];
        } catch (err) {
            console.error('Duplicate lead lookup failed:', err);
            duplicateLeads = [];
        }
    }

    /** Debounced postcode lookup, leads only. */
    function queueDuplicateCheck(postcode: string | null | undefined) {
        if (!isLead) {
            return;
        }
        if (duplicateDebounceTimer) {
            clearTimeout(duplicateDebounceTimer);
        }
        duplicateDebounceTimer = setTimeout(() => checkPostcodeDuplicates(postcode), 400);
    }

    /** Statuses not available when creating a new lead (require a converted job) */
    const newLeadExcludedStatuses = ['booked', ...statusesThatRequireJob];

    type Props = {
        blocks: (Blocks & { fields: Field[] })[];
        show: boolean;
        entityTitle: string;
        createRoute: string;
        relatedRecordId?: string | null;
        relatedRecordLabel?: string | null;
        initialValues?: Record<string, any>;
    };

    let {blocks, show = $bindable(), createRoute, entityTitle, relatedRecordId, relatedRecordLabel, initialValues}: Props = $props();

    // Track the selected status for leads
    const isLead = $derived(entityTitle === 'Lead');
    const selectedStatus = $derived(isLead ? $createForm.status : null);

    // Helper to check if a field should be required based on lead status
    // Field is required if EITHER the field definition says so OR the status rules say so
    function shouldFieldBeRequired(field: Field): boolean {
        const fieldRequired = field.required || false;

        if (!isLead) return fieldRequired;

        const statusRequired = isFieldRequiredForStatus(field.column_name, selectedStatus);

        // Return true if EITHER condition is true
        return fieldRequired || statusRequired;
    }

    // Fields to auto-populate from customer selection
    const customerAutoFillFields = [
        'first_name',
        'last_name',
        'email',
        'phone',
        'organisation_name'
    ] as const;

    function close() {
        show = false;
        duplicateLeads = [];
    }

    function submit(e) {
        $createForm.processing = true;
        e.preventDefault();

        // Normalize checkbox values (convert null/undefined to false)
        const normalizedData = normalizeCheckboxValues($createForm.data(), allFields);

        // If a contact was selected for an organisation, add to customers
        if (selectedContactId && selectedOrganisationId && isLead) {
            normalizedData.customers = normalizedData.customers || [];
            if (!normalizedData.customers.includes(selectedContactId)) {
                normalizedData.customers.push(selectedContactId);
            }
        }

        // Transform flat form data to nested structure for address and site_survey
        const transformedData = transformToNestedStructure(normalizedData);

        // Use router.post to send the transformed data
        router.post(route(createRoute), transformedData, {
            onSuccess: () => {
                close();
                $createForm.reset();
                toast.success(`${entityTitle} created successfully!`);
            },
            onError: (errors) => {
                // Set errors on the form so fields get highlighted
                $createForm.errors = errors;
                for (const message of Object.values(errors)) {
                    toast.error(`Error creating ${entityTitle} record. ${message as string}`);
                }
                $createForm.processing = false;
            },
            onFinish: () => {
                $createForm.processing = false;
            }
        });
    }

    // Flatten all fields for easy access
    const allFields = blocks.flatMap(block => block.fields);

    let createFormInner: Record<string, any> = {}
    for (const block of blocks) {
        for (const field of block.fields) {
            // Initialize checkboxes with false instead of null
            if (field.ui_type === 'checkbox') {
                createFormInner[field.column_name] = initialValues?.[field.column_name] === true;
            } else {
                createFormInner[field.column_name] = initialValues?.[field.column_name] ?? null;
            }
        }
    }

    // Default status to 'new' for leads if not provided
    if (isLead && !initialValues?.status) {
        createFormInner['status'] = 'new';
    }

    let createForm = $state(useForm(createFormInner));

    let isSelectJobOpen = $state(false);
    let jobLabel: string | null = $state(relatedRecordLabel ?? null);
    let selectedRelatedJobs = $state(new SvelteMap());

    let isSelectCustomerOpen = $state(false);
    let customerLabel: string | null = $state(relatedRecordLabel ?? null);
    let isSavingCustomer = $state(false);

    // Organisation contact picker state
    let selectedCustomerType = $state<'contact' | 'organisation' | null>(null);
    let selectedOrganisationId = $state<string | null>(null);
    let selectedOrganisationName = $state<string>('');

    // Contact picker for organisation
    let isSelectContactOpen = $state(false);
    let selectedContactId = $state<string | null>(null);
    let selectedContactLabel = $state<string | null>(null);

    // Inline add contact form
    let showAddContactForm = $state(false);
    let addContactForm = $state({
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
    });
    let isSavingOrgContact = $state(false);

    // Check if customer fields are filled for "Save as Contact" button
    // Requires first_name and email (last_name is optional)
    let canSaveAsContact = $derived(() => {
        return (
            $createForm.first_name?.trim() &&
            $createForm.email?.trim() &&
            !$createForm.is_primary // Only show if no primary customer is linked
        );
    });

    // Check if organisation name and email are filled for "Save as Organisation" button
    let canSaveAsOrganisation = $derived(() => {
        return (
            $createForm.organisation_name?.trim() &&
            $createForm.email?.trim() &&
            !$createForm.is_primary // Only show if no primary customer is linked
        );
    });

    // Estimated job date ↔ duration auto-calculation (leads only)
    createDateDurationSync({
        getStartDate: () => $createForm.estimated_job_start,
        getEndDate: () => $createForm.estimated_job_end,
        getDuration: () => $createForm.estimated_hire_duration_days,
        setEndDate: (date) => { $createForm.estimated_job_end = date; },
        setDuration: (duration) => { $createForm.estimated_hire_duration_days = duration; },
        isActive: () => entityTitle === 'Lead',
    });

    // Additional hire date ↔ duration auto-calculation
    createDateDurationSync({
        getStartDate: () => $createForm.additional_hire_start_date,
        getEndDate: () => $createForm.additional_hire_end_date,
        getDuration: () => $createForm.additional_hire_duration?.toString(),
        setEndDate: (date) => { $createForm.additional_hire_end_date = date; },
        setDuration: (duration) => { $createForm.additional_hire_duration = duration; },
    });

    // Warn about existing leads at the same postcode as the user types it.
    // Reads postcode only and writes to the separate `duplicateLeads` state,
    // so there is no form-mutation feedback loop.
    $effect(() => {
        queueDuplicateCheck($createForm.postcode);
    });

    // Check if form has address fields
    // let hasAddressFields = $derived(() => {
    //     const addressFieldNames = ['address_line_one', 'postcode', 'city'];
    //     return blocks.some(block =>
    //         block.fields.some(field =>
    //             addressFieldNames.includes(field.column_name) && field.editable
    //         )
    //     );
    // });

    // Handle address selection from lookup
    function handleAddressSelect(address: {
        line_1: string;
        line_2: string;
        city: string;
        postcode: string;
        county: string;
    }) {
        $createForm.address_line_one = address.line_1;
       // $createForm.address_line_two = address.line_2;
        $createForm.city = address.city;
        $createForm.postcode = address.postcode;
        $createForm.county = address.county;
        queueDuplicateCheck(address.postcode);
    }

    // Save contact information as a new customer
    function saveAsContact() {
        isSavingCustomer = true;

        router.post(route('customers.store'), {
            type: 'contact',
            first_name: $createForm.first_name,
            last_name: $createForm.last_name,
            email: $createForm.email,
            phone: $createForm.phone,
            organisation_name: $createForm.organisation_name || null,
        }, {
            preserveScroll: true,
            preserveState: true,
            headers: {
                'X-From-Modal': 'true'
            },
            onSuccess: (page) => {
                const flash = page.props.flash as any;
                if (flash?.customer) {
                    // Link the new customer as primary customer
                    $createForm.is_primary = flash.customer.id;
                    customerLabel = `${flash.customer.first_name} ${flash.customer.last_name}`;
                    toast.success('Contact saved successfully!');
                }
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error(`Error creating contact: ${message as string}`);
                }
            },
            onFinish: () => {
                isSavingCustomer = false;
            }
        });
    }

    // Save organisation information as a new customer (organisation type)
    function saveAsOrganisation() {
        isSavingCustomer = true;

        router.post(route('customers.store'), {
            type: 'organisation',
            organisation_name: $createForm.organisation_name,
            email: $createForm.email,
            phone: $createForm.phone || null,
        }, {
            preserveScroll: true,
            preserveState: true,
            headers: {
                'X-From-Modal': 'true'
            },
            onSuccess: (page) => {
                const flash = page.props.flash as any;
                if (flash?.customer) {
                    // Link the new organisation as primary customer
                    $createForm.is_primary = flash.customer.id;
                    customerLabel = flash.customer.organisation_name || flash.customer.name;
                    toast.success('Organisation saved successfully!');
                }
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error(`Error creating organisation: ${message as string}`);
                }
            },
            onFinish: () => {
                isSavingCustomer = false;
            }
        });
    }

    // Reset contact selection when organisation changes
    function resetContactSelection() {
        selectedContactId = null;
        selectedContactLabel = null;
        showAddContactForm = false;
        addContactForm = { first_name: '', last_name: '', email: '', phone: '' };
    }

    // Save new contact for the selected organisation
    function saveNewOrgContact() {
        if (!selectedOrganisationId) return;

        isSavingOrgContact = true;

        router.post(route('customers.store'), {
            type: 'contact',
            first_name: addContactForm.first_name,
            last_name: addContactForm.last_name,
            email: addContactForm.email,
            phone: addContactForm.phone || null,
            organisation_id: selectedOrganisationId,
        }, {
            preserveScroll: true,
            preserveState: true,
            headers: {
                'X-From-Modal': 'true'
            },
            onSuccess: (page) => {
                const flash = page.props.flash as any;
                if (flash?.customer) {
                    selectedContactId = flash.customer.id;
                    selectedContactLabel = `${flash.customer.first_name} ${flash.customer.last_name}`;
                    showAddContactForm = false;
                    addContactForm = { first_name: '', last_name: '', email: '', phone: '' };
                    toast.success('Contact saved and linked to organisation!');
                }
            },
            onError: (errors) => {
                for (const message of Object.values(errors)) {
                    toast.error(`Error creating contact: ${message as string}`);
                }
            },
            onFinish: () => {
                isSavingOrgContact = false;
            }
        });
    }

</script>
{#if show}
    <Modal title="Add New { entityTitle }" onClose={close}>
        <FormContainer>
            <form onsubmit={submit}>
                {#snippet duplicateWarning()}
                    <Alert variant="default" class="col-span-2 border-2 border-amber-400 text-amber-600 [&>svg]:text-amber-500 dark:border-amber-500/70 dark:text-amber-400">
                        <AlertTitle>
                            Possible duplicate — {duplicateLeads.length} existing lead{duplicateLeads.length === 1 ? '' : 's'} at this postcode
                        </AlertTitle>
                        <AlertDescription>
                            <ul class="mt-1 flex flex-col gap-2">
                                {#each duplicateLeads as dupe (dupe.id)}
                                    <li class="flex flex-col">
                                        {#if dupe.address}
                                            <span class="text-foreground">{dupe.address}</span>
                                        {/if}
                                        {#if dupe.customers.length > 0}
                                            <span class="text-muted-foreground">Customer{dupe.customers.length === 1 ? '' : 's'}: {dupe.customers.join(', ')}</span>
                                        {/if}
                                        {#if dupe.start_date}
                                            <span class="text-muted-foreground">Start date: {formatDateLocale(dupe.start_date, true)}</span>
                                        {/if}
                                        <!-- eslint-disable-next-line svelte/no-navigation-without-resolve -- Inertia/ziggy route(), opened in a new tab -->
                                        <a href={route('leads.show', { lead: dupe.id })} target="_blank" rel="noopener noreferrer" class="w-fit text-link underline">
                                            View {dupe.reference || dupe.name} →
                                        </a>
                                    </li>
                                {/each}
                            </ul>
                        </AlertDescription>
                    </Alert>
                {/snippet}
                <!-- Address Lookup (if form has address fields) -->
                <!--{#if hasAddressFields()}-->
                <!--    <FormBlock label="Address Lookup">-->
                <!--       -->
                <!--    </FormBlock>-->
                <!--{/if}-->

                {#each blocks as block (block.id)}

                        <FormBlock label={block.label}>
                            {#if block.label === 'Address'}
                                <AddressLookup onAddressSelect={handleAddressSelect} />
                            {/if}
                            <!-- For Lead Customer Information block: show Existing Customer picker first -->
                            {#if isLead && block.label === 'Customer Information'}
                                {@const primaryField = block.fields.find(f => f.column_name === 'is_primary')}
                                {#if primaryField?.editable}
                                    <div class="col-span-2">
                                        <RelatedSingle
                                            label="Existing Customer"
                                            bind:value={$createForm[primaryField.column_name]}
                                            error={$createForm.errors[primaryField.column_name]}
                                            bind:isOpen={isSelectCustomerOpen}
                                            bind:valLabel={customerLabel}
                                        >
                                            <CustomerTable
                                                view="related"
                                                onRowClick={(row) => {
                                                    $createForm[primaryField.column_name] = row?.id ?? null;
                                                    customerLabel = row?.name ?? '';

                                                    // Track if it's an organisation
                                                    if (row?.type === 'organisation') {
                                                        selectedCustomerType = 'organisation';
                                                        selectedOrganisationId = row.id;
                                                        selectedOrganisationName = row.organisation_name || row.name;
                                                        // Reset contact selection when org changes
                                                        resetContactSelection();
                                                    } else {
                                                        selectedCustomerType = row ? 'contact' : null;
                                                        selectedOrganisationId = null;
                                                        selectedOrganisationName = '';
                                                        resetContactSelection();
                                                    }

                                                    // Auto-populate matching fields from customer
                                                    customerAutoFillFields.forEach(fieldName => {
                                                        if (row?.[fieldName]) {
                                                            $createForm[fieldName] = row[fieldName];
                                                        }
                                                    });

                                                    isSelectCustomerOpen = false;
                                                }}
                                            />
                                        </RelatedSingle>
                                    </div>

                                    <!-- Contact Picker (only show when organisation is selected) -->
                                    {#if selectedCustomerType === 'organisation' && selectedOrganisationId}
                                        <div class="col-span-2 mt-2">
                                            <RelatedSingle
                                                label="Select {selectedOrganisationName} Contact"
                                                bind:value={selectedContactId}
                                                bind:isOpen={isSelectContactOpen}
                                                bind:valLabel={selectedContactLabel}
                                            >
                                                <CustomerTable
                                                    view="related"
                                                    typeFilter="contact"
                                                    relatedData={{
                                                        column: 'organisation_id',
                                                        record_id: selectedOrganisationId
                                                    }}
                                                    onRowClick={(row) => {
                                                        selectedContactId = row?.id ?? null;
                                                        selectedContactLabel = row?.name ?? '';
                                                        isSelectContactOpen = false;
                                                    }}
                                                />
                                            </RelatedSingle>

                                            <!-- Add New Contact Option -->
                                            <div class="mt-2">
                                                {#if !showAddContactForm}
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onclick={() => showAddContactForm = true}
                                                    >
                                                        + Add New Contact for {selectedOrganisationName}
                                                    </Button>
                                                {:else}
                                                    <!-- Inline Add Contact Form -->
                                                    <div class="mt-3 p-4 border rounded-lg bg-muted/30">
                                                        <h4 class="text-sm font-medium mb-3">Add New Contact for {selectedOrganisationName}</h4>
                                                        <div class="grid grid-cols-2 gap-3">
                                                            <Input
                                                                field={{ label: 'First Name', column_name: 'first_name', required: true }}
                                                                bind:value={addContactForm.first_name}
                                                            />
                                                            <Input
                                                                field={{ label: 'Last Name', column_name: 'last_name' }}
                                                                bind:value={addContactForm.last_name}
                                                            />
                                                            <Input
                                                                field={{ label: 'Email', column_name: 'email', required: true }}
                                                                bind:value={addContactForm.email}
                                                            />
                                                            <Input
                                                                field={{ label: 'Phone', column_name: 'phone' }}
                                                                bind:value={addContactForm.phone}
                                                            />
                                                        </div>
                                                        <div class="flex gap-2 mt-3">
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                onclick={() => {
                                                                    showAddContactForm = false;
                                                                    addContactForm = { first_name: '', last_name: '', email: '', phone: '' };
                                                                }}
                                                            >
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                onclick={saveNewOrgContact}
                                                                disabled={isSavingOrgContact || !addContactForm.first_name || !addContactForm.email}
                                                            >
                                                                {isSavingOrgContact ? 'Saving...' : 'Save Contact'}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                {/if}
                                            </div>
                                        </div>
                                    {/if}

                                    <!-- Divider with "OR Add New Customer" - only show when no existing customer selected -->
                                    {#if !$createForm.is_primary}
                                        <div class="col-span-2 flex items-center gap-4 my-2">
                                            <div class="flex-1 h-px bg-border"></div>
                                            <div class="grid grid-cols-1">
                                                <span class="text-md text-muted-foreground font-medium text-center">OR</span>
                                                <span class="text-sm text-muted-foreground font-medium">add new customer</span>
                                            </div>

                                            <div class="flex-1 h-px bg-border"></div>
                                        </div>
                                    {/if}
                                {/if}
                            {/if}
                            {#each block.fields as field (field.id)}
                                {@const fieldWithRequired = { ...field, required: shouldFieldBeRequired(field) }}
                                {@const isCustomerInfoField = customerAutoFillFields.includes(field.column_name as typeof customerAutoFillFields[number])}
                                <!-- Skip is_primary field for leads as it's rendered above -->
                                <!-- Skip customer info fields when an existing customer is selected -->
                                {#if field.editable && !(isLead && block.label === 'Customer Information' && field.column_name === 'is_primary') && !(isLead && block.label === 'Customer Information' && isCustomerInfoField && $createForm.is_primary)}
                                    {#if field.ui_type === 'multi_related'}
                                        <MultiRelated
                                            label={field.label}
                                            bind:value={$createForm[field.column_name]}
                                            error={$createForm.errors[field.column_name]}
                                            bind:isOpen={isSelectJobOpen}
                                            bind:valLabel={jobLabel}
                                        >
                                            <JobTable
                                                view="multi_related"
                                                onRowClick={(row) => {
                                                        // $form.customer_id = row?.id ?? null;
                                                        // customerLabel = row?.first_name ?? '';
                                                        // isSelectCustomerOpen = false;
                                                    }}
                                                bind:selectedJobs={selectedRelatedJobs}
                                                onSubmitClick={() => {
                                                        $createForm.related_jobs = selectedRelatedJobs.keys().toArray();
                                                        jobLabel = Array.from(selectedRelatedJobs.values()).join(", ");
                                                        isSelectJobOpen = false;
                                                    }}
                                            />
                                        </MultiRelated>
                                    {:else if field.ui_type === 'select'}
                                        {@const options = isLead && field.column_name === 'status'
                                            ? parseFieldOptions(field).filter((o: {value: string}) => !newLeadExcludedStatuses.includes(o.value))
                                            : parseFieldOptions(field)}
                                        <Select
                                            label={field.label}
                                            bind:value={$createForm[field.column_name]}
                                            {options}
                                            error={$createForm.errors[field.column_name]}
                                            field={fieldWithRequired}
                                        />
                                    {:else if field.ui_type === 'textarea'}
                                        <Textarea
                                            label={field.label}
                                            bind:value={$createForm[field.column_name]}
                                            error={$createForm.errors[field.column_name]}
                                            field={fieldWithRequired}
                                        />
                                    {:else if field.ui_type === 'date'}
                                        <DateOnly
                                            label={field.label}
                                            bind:value={$createForm[field.column_name]}
                                            error={$createForm.errors[field.column_name]}
                                        />
                                    {:else if field.ui_type === 'datetime'}
                                        <DateTime
                                            label={field.label}
                                            bind:value={$createForm[field.column_name]}
                                            error={$createForm.errors[field.column_name]}
                                            field={fieldWithRequired}
                                        />
                                    {:else if field.ui_type === 'checkbox'}
                                        <Checkbox
                                            bind:checked={$createForm[field.column_name]}
                                            label={field.label}
                                            error={$createForm.errors[field.column_name]}
                                        />
                                    {:else}
                                        <Input
                                            field={fieldWithRequired}
                                            bind:value={$createForm[field.column_name]}
                                            error={$createForm.errors[field.column_name]}
                                            onkeydown={() => {}}
                                        />
                                    {/if}
                                    {#if isLead && field.column_name === 'postcode' && duplicateLeads.length > 0}
                                        {@render duplicateWarning()}
                                    {/if}
                                {/if}
                            {/each}
                            <!-- Save as Contact / Organisation buttons for Customer Information block -->
                            {#if block.label === 'Customer Information' && (canSaveAsContact() || canSaveAsOrganisation())}
                                <div class="mb-4 flex gap-2">
                                    {#if canSaveAsContact()}
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onclick={saveAsContact}
                                            disabled={isSavingCustomer}
                                            class="flex-1"
                                        >
                                            {#if isSavingCustomer}Saving...{:else}Save as Contact{/if}
                                        </Button>
                                    {/if}
                                    {#if canSaveAsOrganisation()}
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onclick={saveAsOrganisation}
                                            disabled={isSavingCustomer}
                                            class="flex-1"
                                        >
                                            {#if isSavingCustomer}Saving...{:else}Save as Organisation{/if}
                                        </Button>
                                    {/if}
                                </div>
                            {/if}
                        </FormBlock>
                {/each}

                <div class="mt-5 flex justify-end gap-2">
                    <Button type="button" onclick={close}>Cancel</Button>
                    <Button type="submit" disabled={$createForm.processing}>
                        {#if $createForm.processing}Saving...{:else}Save{/if}
                    </Button>
                </div>
            </form>
        </FormContainer>
    </Modal>
{/if}
